import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFeatureFlag } from '@kameleoon/react-sdk';
import { isFlagValid, isFlagVariant } from '../utils';
import { useKameleoonState } from './useKameleoonState';
import { type CustomDataName, useAddCustomData } from './useAddCustomData';
import { useUpdatePageView } from './useUpdatePageView';
import type { KameleoonFlagKey, KameleoonFlagValue } from '../types';

/**
 * Options which could be send for targeting during flag evaluation
 */
interface FlagEvaluationOptions {
    /**
     * sends the current url and title
     */
    updatePageView?: boolean;
    customData?: {
        name: CustomDataName;
        value: string;
    };
}

const defaultOptions: FlagEvaluationOptions = {
    updatePageView: true,
};

type FlagVariantData =
    | {
          isLoading: true;
      }
    | {
          isLoading: false;
          isVariant: boolean;
          value: KameleoonFlagValue;
      };

/**
 * @param options - Options which could be send for targeting during flag evaluation
 */
export const useFeatureFlagValue = (
    flag: KameleoonFlagKey,
    options: FlagEvaluationOptions = defaultOptions,
): FlagVariantData => {
    const [value, setValue] = useState<KameleoonFlagValue | null>(null);
    const { getFeatureFlagVariationKey } = useFeatureFlag();

    const updatePageView = useUpdatePageView();
    const addCustomData = useAddCustomData();

    const { state, updateFeaturesList } = useKameleoonState();

    const resolvedOptions = useMemo(
        () => ({
            ...defaultOptions,
            ...options,
        }),
        // The purpose of this is giving possibility to give options as an object literal
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [options.updatePageView, options.customData?.value, options.customData?.name],
    );

    const addFeatureToContext = useCallback(
        (featureKey: KameleoonFlagKey, featureValue: KameleoonFlagValue) => {
            updateFeaturesList((features) => {
                if (features[featureKey] === featureValue) {
                    return features;
                }

                return {
                    ...features,
                    [featureKey]: featureValue,
                };
            });
        },
        [updateFeaturesList],
    );

    useEffect(() => {
        if (state.status === 'loading') {
            setValue(null);

            return;
        }

        if (state.status === 'failed') {
            setValue('off');

            return;
        }

        if (!state.visitorCode) {
            return;
        }

        if (resolvedOptions?.updatePageView) {
            updatePageView();
        }

        if (resolvedOptions?.customData) {
            addCustomData(resolvedOptions.customData);
        }

        let flagValue: KameleoonFlagValue = 'off';

        try {
            const value = getFeatureFlagVariationKey(state.visitorCode, flag) as KameleoonFlagValue;
            flagValue = isFlagValid(value) ? value : 'off';
            if (flagValue !== 'off') {
                addFeatureToContext(flag, flagValue);
            }
        } catch (e) {
            flagValue = 'off';
            console.error(e);
        }

        setValue(flagValue);
    }, [
        addFeatureToContext,
        flag,
        getFeatureFlagVariationKey,
        resolvedOptions,
        state,
        addCustomData,
        updatePageView,
    ]);

    if (value === null || state.status === 'loading') {
        return { isLoading: true };
    }

    const isVariant = isFlagVariant(value);

    return { isVariant, value, isLoading: false };
};
